import { ClockIcon } from '@heroicons/react/24/outline';
import { SideRailCollapseButton } from '../../side-rail/SideRailCollapseButton';
import type { TimelineEvent } from '../actions/types';
import type { JourneySegment } from '../../../services/timeline';
import { TimelineRailEvent } from './TimelineRailEvent';
import { JourneyView } from './JourneyView';
import { TimelineModeToggle, type TimelineMode } from './TimelineModeToggle';

interface TimelineRailExpandedProps {
    events: TimelineEvent[];
    total: number;
    isLoading: boolean;
    isError: boolean;
    caseAgeLabel: string | null;
    onCollapse: () => void;
    mode: TimelineMode;
    onModeChange: (mode: TimelineMode) => void;
    journeySegments: JourneySegment[];
    journeyLoading: boolean;
    journeyError: boolean;
}

function TimelineRailExpanded({
    events,
    total,
    isLoading,
    isError,
    caseAgeLabel,
    onCollapse,
    mode,
    onModeChange,
    journeySegments,
    journeyLoading,
    journeyError
}: TimelineRailExpandedProps) {
    return (
        <aside className="w-1/4 min-w-[300px] max-w-[340px] bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.08)] flex flex-col flex-shrink-0 overflow-hidden">
            <header className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <ClockIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-gray-900 font-inter">Timeline</h3>
                    {mode === 'activity' && total > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            {total}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <TimelineModeToggle mode={mode} onChange={onModeChange} />
                    <SideRailCollapseButton
                        side="right"
                        panelName="timeline"
                        onCollapse={onCollapse}
                    />
                </div>
            </header>

            <div className="flex-1 min-h-0 overflow-auto px-3 py-3">
                {mode === 'journey' ? (
                    <JourneyView
                        segments={journeySegments}
                        isLoading={journeyLoading}
                        isError={journeyError}
                    />
                ) : (
                    <>
                        {isLoading && (
                            <div className="flex items-center justify-center py-6">
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                            </div>
                        )}

                        {!isLoading && isError && (
                            <div className="text-center py-6">
                                <div className="text-xs text-red-500 font-inter">
                                    Failed to load timeline
                                </div>
                            </div>
                        )}

                        {!isLoading && !isError && events.length === 0 && (
                            <div className="text-center py-6">
                                <div className="text-xs text-gray-400 font-inter">
                                    No events yet
                                </div>
                            </div>
                        )}

                        {!isLoading && !isError && events.length > 0 && (
                            <ul className="relative">
                                {events.map((event, index) => (
                                    <TimelineRailEvent
                                        key={event.id}
                                        event={event}
                                        isLast={index === events.length - 1}
                                    />
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            {caseAgeLabel && (
                <footer className="px-3 py-2 border-t border-gray-100 bg-gray-50/40 flex items-center flex-shrink-0">
                    <span className="text-[10px] text-gray-500 font-inter">{caseAgeLabel}</span>
                </footer>
            )}
        </aside>
    );
}

export { TimelineRailExpanded };
